// re.rejouis.fr — ACCUEIL recherche-first, structure inspirée de la page revente
// Back Market : accroche centrée → recherche → réassurance → « que veux-tu
// revendre ? » en liste simple → modèles populaires (carrousel) → FAQ.
// La recherche suit directement l'accroche ; les 3 étapes tiennent en une ligne de pictos juste
// dessous (allègement du 2026-10-28). Pendant la recherche (q ≥ 2), tout le reste se masque.
import { useEffect, useRef, useState } from 'react';
import { LogoRe, Thumb, Icons, Sticker, Illus } from '../components/ui.jsx';
import PanierBar from '../components/PanierBar.jsx';
import RechercheModele from '../components/RechercheModele.jsx';
import { modelesPopulaires, offresDuMoment, disponibiliteVirement } from '../lib/api.js';
import { pctVirement } from '../lib/rachat.js';
import { CarteRecherche } from './Recherches.jsx';
import { suivre } from '../lib/posthog.js';
import { boostPrio } from '../lib/prio.js';
import { REFERENCEMENT_ACTIF } from '../lib/flags.js';

const PAD = '0 18px';

function Titre({ children, sous }) {
  return (
    <div style={{ padding: PAD, marginBottom: 12 }}>
      <h2 className="t-display" style={{ margin: 0, fontSize: 21 }}>{children}</h2>
      {sous ? <div style={{ fontSize: 13, color: 'var(--stone)', marginTop: 3 }}>{sous}</div> : null}
    </div>
  );
}

// Ligne de choix façon Back Market : mini-illustration + libellé, rien d'autre.
function Choix({ illus, label, onClick }) {
  return (
    <button onClick={onClick} className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%', minWidth: 0,
      textAlign: 'center', padding: '16px 4px 14px' }}>
      <Illus nom={illus} size={48} />
      <span style={{ fontWeight: 600, fontSize: 'clamp(12px, 3.3vw, 14px)', lineHeight: 1.3 }}>{label}</span>
    </button>
  );
}

// Conteneur défilant horizontal (carrousels).
function Carrousel({ children }) {
  return (
    <div className="no-scrollbar" style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '2px 18px 6px', scrollSnapType: 'x mandatory', scrollPaddingLeft: 18 }}>
      {children}
    </div>
  );
}

// Carte modèle populaire : grande image, nom, « Estimer ». Pas de montant (retiré
// le 2026-10-16, comme sur les tuiles de recherche : le « jusqu'à X € » décevait).
function CartePopulaire({ p, dansPanier, onChoisir }) {
  return (
    <button onClick={() => onChoisir(p)} className="card" style={{ flex: '0 0 158px', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 8, padding: '14px 12px 14px', textAlign: 'center', position: 'relative' }}>
      {dansPanier ? (
        <span style={{ position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: 999, background: 'var(--mint)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icons.check size={14} sw={2.4} /></span>
      ) : null}
      <Thumb produit={p} size={112} radius={12} />
      <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 34 }}>
        {p.titre}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, fontSize: 13, fontWeight: 700, color: 'var(--rouge)' }}>
        Estimer <Icons.chev size={14} sw={2.4} />
      </div>
    </button>
  );
}

function Question({ q, children }) {
  return (
    <details className="faq">
      <summary>{q}<Icons.chev size={16} style={{ flexShrink: 0, color: 'var(--stone)' }} /></summary>
      <div style={{ padding: '0 2px 16px', fontSize: 13.5, lineHeight: 1.55, color: 'var(--encre-doux)' }}>{children}</div>
    </details>
  );
}

const lien = { color: 'var(--rouge)', fontWeight: 600, textDecoration: 'underline', padding: 0 };

export default function Accueil({ go, panier, client, onChoisir }) {
  const [q, setQ] = useState('');
  const [populaires, setPopulaires] = useState([]);
  const [recherches, setRecherches] = useState([]);
  const [virementOk, setVirementOk] = useState(false);
  const scrollRef = useRef(null);
  const enRecherche = q.trim().length >= 2;
  const idsPanier = new Set(panier.map((l) => l.produitId));

  useEffect(() => {
    let vivant = true;
    modelesPopulaires().then((r) => { if (vivant) setPopulaires(r.produits || []); }).catch(() => {});
    // Offres du moment (boost et virement) ; sans budget virement ce mois-ci, seuls les boostés.
    Promise.all([offresDuMoment(), disponibiliteVirement().catch(() => ({ disponible: false }))])
      .then(([r, d]) => {
        if (!vivant) return;
        setVirementOk(!!d?.disponible);
        setRecherches((r.produits || []).filter((p) => boostPrio(p) > 0 || (d?.disponible && pctVirement(p) > 0)));
      })
      .catch(() => {});
    return () => { vivant = false; };
  }, []);

  // Logo : retour à la home propre (recherche vidée, en haut de page).
  const versAccueil = () => {
    setQ('');
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    go('accueil');
  };

  return (
    <div ref={scrollRef} className="no-scrollbar" style={{ height: '100%', overflow: 'auto', padding: '20px 0 120px' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 18px 6px' }}>
        <button onClick={versAccueil} aria-label="Accueil" style={{ padding: 0, display: 'flex' }}><LogoRe size={20} /></button>
        <button onClick={() => go('compte')} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: client ? 'var(--mint-fonce)' : 'var(--stone)' }}>
          <Icons.user size={17} /> {client ? (client.prenom || 'Mon compte') : 'Se connecter'}
        </button>
      </div>

      {/* Accroche centrée, encadrée de deux stickers de la charte */}
      <div style={{ position: 'relative', padding: '58px 22px 22px', textAlign: 'center' }}>
        <Sticker nom="peche" size={50} rot={-12} anim="flotte" style={{ position: 'absolute', left: 8, top: 4 }} />
        <Sticker nom="coeur-etincelle" size={54} rot={10} anim="flotte" style={{ position: 'absolute', right: 8, top: 0, animationDelay: '1.2s' }} />
        <h1 className="t-display" style={{ fontSize: 31, margin: 0 }}>
          Ton jouet mérite<br />une <span style={{ color: 'var(--rouge)' }}>seconde vie</span>
        </h1>
        <p style={{ margin: '12px auto 0', fontSize: 14.5, lineHeight: 1.55, color: 'var(--encre-doux)', maxWidth: 330 }}>
          Cherche ton modèle : ton offre s’affiche tout de suite.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <span className="tag-incline">Envoi offert</span>
          <span className="tag-incline bleu">Bon d’achat ou virement</span>
        </div>
      </div>

      {/* Recherche : directement sous l'accroche. 3 résultats max ici ; Entrée ou
          « Voir les N résultats » ouvre la page de recherche. */}
      <div style={{ padding: '4px 0 0' }}>
        <RechercheModele go={go} panier={panier} onChoisir={onChoisir} q={q} setQ={setQ} limite={3} hero
          onValider={(texte) => go('catalogue', { q: texte })} />
      </div>

      {/* Les 3 étapes en une ligne de pictos, sans phrases à lire */}
      {!enRecherche ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, padding: '18px 16px 0' }}>
          {[
            { st: 'rabbit-etincelle', t: 'Cherche ton modèle' },
            { st: 'boite', t: 'Envoie-le gratuitement' },
            { st: 'confettis', t: 'Bon d’achat ou virement' },
          ].map((e, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 4 }}>
              <Sticker nom={e.st} size={40} />
              <div style={{ fontWeight: 700, fontSize: 12, lineHeight: 1.3 }}><span style={{ color: 'var(--rouge)' }}>{i + 1}.</span> {e.t}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Autres entrées : photo, lot, (référencement), don — visibles même pendant une recherche */}
      <div style={{ paddingTop: 30 }}>
        <Titre>{enRecherche ? 'Pas le bon ? Autres façons de revendre' : 'Autres façons de revendre'}</Titre>
        {/* Blocs côte à côte (une colonne par option, y compris sur mobile) */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${REFERENCEMENT_ACTIF ? 4 : 3}, minmax(0, 1fr))`, gap: 10, padding: PAD }}>
          <Choix illus="photo" label="L’identifier par photo" onClick={() => go('scan')} />
          <Choix illus="lot" label={'J’ai un lot ou un jouet hors catalogue'} onClick={() => go('lot')} />
          {REFERENCEMENT_ACTIF ? <Choix illus="ajout" label="Mon modèle n’est pas là" onClick={() => go('referencement', {})} /> : null}
          <Choix illus="don" label="Juste m’en débarrasser (don)" onClick={() => go('don')} />
        </div>
      </div>

      {!enRecherche ? (
        <>

          {/* Offres du moment (Prio boosté, virement) : 3 premières + lien vers /offres */}
          {recherches.length ? (
            <div style={{ paddingTop: 30 }}>
              <Titre sous={virementOk ? 'Crédit boosté ou virement possible' : 'On en manque : ton crédit est boosté pour un temps limité'}>Offres du moment</Titre>
              <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {recherches.slice(0, 3).map((p) => (
                  <CarteRecherche key={p.id} p={p} virement={virementOk} dansPanier={idsPanier.has(p.id)} onChoisir={(x) => {
                    suivre('wanted_model_selected', { boost_pct: Math.round(boostPrio(x) * 100), source: 'accueil' });
                    onChoisir(x);
                  }} />
                ))}
                {recherches.length > 3 ? (
                  <button onClick={() => go('offres')} className="btn btn-secondary btn-block">
                    Voir les {recherches.length} offres
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Modèles populaires à reprendre */}
          {populaires.length ? (
            <div style={{ paddingTop: 30 }}>
              <Titre sous="Les jouets qu’on reprend le plus volontiers">Modèles populaires</Titre>
              <Carrousel>
                {populaires.map((p) => (
                  <CartePopulaire key={p.id} p={p} dansPanier={idsPanier.has(p.id)} onChoisir={onChoisir} />
                ))}
              </Carrousel>
            </div>
          ) : null}

          {/* FAQ */}
          <div style={{ padding: '34px 18px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <h2 className="t-display" style={{ margin: 0, fontSize: 21 }}>FAQ</h2>
              <Sticker nom="sourire" size={34} rot={-8} />
            </div>
            <div style={{ fontSize: 13, color: 'var(--stone)', marginBottom: 6 }}>Les questions que tout le monde se pose</div>
            <Question q="Comment mon crédit est-il calculé ?">
              Il dépend du modèle, de son état et de la demande du moment.{' '}
              <button onClick={() => go('prix-reprise')} style={lien}>Voir le détail du calcul</button>
            </Question>
            <Question q="Est-ce que je suis payé en argent ?">
              Le plus souvent en <strong>bon d’achat Rejouis</strong>, versé 2 jours après réception, sans date limite.
              Sur certains modèles qu’on recherche, tu peux choisir un <strong>virement</strong> (70 % du bon d’achat) au moment de l’estimation.
            </Question>
            <Question q="Mon modèle n’est pas dans la liste ?">
              {REFERENCEMENT_ACTIF ? (
                <>
                  Essaie de l’identifier par photo, ou colle le lien du produit neuf pour demander son référencement : on crée la fiche en quelques secondes.{' '}
                  <button onClick={() => go('referencement', {})} style={lien}>Demander le référencement</button>
                </>
              ) : (
                <>
                  On reprend à l’unité les modèles de notre catalogue (2 000+ références). Introuvable par son nom ?{' '}
                  <button onClick={() => go('scan')} style={lien}>Essaie par photo</button>.
                  Toujours rien (jouet ancien, sans marque…) ? Propose-le dans un <strong>lot</strong> avec tes autres jouets :
                  une description et une photo prise par toi suffisent, on l’évalue à la main, même pour un seul jouet.{' '}
                  <button onClick={() => go('lot')} style={lien}>Proposer un lot</button>
                  <br />Tu préfères t’en séparer sans attendre ? Tu peux nous en faire <strong>don</strong>.{' '}
                  <button onClick={() => go('don')} style={lien}>Faire un don</button>
                </>
              )}
            </Question>
            {REFERENCEMENT_ACTIF ? <Question q="Mon jouet n’a pas de référence connue ?">
              Jouet ancien, sans marque, ou introuvable en ligne (pas de lien vers le produit neuf) ? Propose-le dans un <strong>lot</strong> :
              une description et une photo de tes jouets (prise par toi, pas trouvée sur internet) suffisent, on l’évalue à la main, même pour un seul jouet.{' '}
              <button onClick={() => go('lot')} style={lien}>Proposer un lot</button>
              <br />Tu préfères t’en séparer sans attendre ? Tu peux nous en faire <strong>don</strong>.{' '}
              <button onClick={() => go('don')} style={lien}>Faire un don</button>
            </Question> : null}
            <Question q="J’ai beaucoup de jouets à revendre">
              Envoie-nous une photo de ton lot prise par toi : on l’évalue à la main.{' '}
              <button onClick={() => go('lot')} style={lien}>Proposer un lot</button>
            </Question>
            <Question q="Et s’il ne vaut pas grand-chose ?">
              Tu peux simplement nous le donner, sans estimation ni crédit : il retrouvera une seconde vie. Attention, dans ce cas l’envoi n’est pas gratuit.{' '}
              <button onClick={() => go('don')} style={lien}>Faire un don</button>
            </Question>
          </div>
        </>
      ) : null}

      <PanierBar panier={panier} go={go} />
    </div>
  );
}
